import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["attachTo", "children", "stepKey", "tourKey", "content", "childOnActive", "onClickCloseButton", "onOpenComplete", "onOpenStart", "onCloseStart", "open", "pulser", "scrollTo", "scrollToTarget", "showCloseButton", "showSteps", "stepProgress", "toggleOnTargetVisibilityChange", "beforeStart", "width"];
import I18n from 'I18n';
import { Children, cloneElement, createRef, isValidElement, Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import Raven from 'raven-js';
import { Popover, PopoverBody } from 'foundations-components/transitional/components/Popover';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { ToolBarContext } from 'foundations-components/transitional/components/ToolBar';
import TourContext from '../contexts/TourContext';
import UITourPulserModern from '../pulser/UITourPulser';
import { hasRedesignGate } from '../utils/redesignGate';
import { ONBOARDING_COLOR } from '../constants/colors';
import { OnFillLinkOverride } from '../constants/styles';
import handleCallback from '../lib/handleCallback';
import { deactivateTour } from '../lib/deactivateTour';
import { getAttachedElement, isElementVisible } from '../utils/elementUtils';
import OnFillCloseButton from '../button/OnFillCloseButton';
import TourStepChildren from './components/TourStepChildren';
import { waitForElementToAppear } from '../utils/observerUtils';
import UITourStepLegacy from './UITourStep.legacy';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BUTTONS_AND_FIRST_ORDER_TAB_ELEMENTS_SELECTOR = '[data-button-use="primary"],button,[data-role="button"],[tabindex="0"]';

/**
 * Very simple space replacement for stepKey
 *
 * As we need the stepKey to be used with aria-labelledby all spaces should be replaced
 */
const hyphenateKey = stepKey => stepKey.replace(' ', '-');
const focusButtonElement = popoverId => {
  const popover = document.getElementById(popoverId);
  if (!popover) {
    return;
  }
  const focusableElements = popover.querySelectorAll(BUTTONS_AND_FIRST_ORDER_TAB_ELEMENTS_SELECTOR);
  if (focusableElements.length) {
    // focus the last interactive element:
    // this is to avoid selecting the close button unless it's the only choice
    focusableElements[focusableElements.length - 1].focus({
      preventScroll: true
    });
  }
};
function getContentPart(content, key) {
  if (content && typeof content === 'object' && key in content) {
    return content[key];
  }
  return undefined;
}
class UITourStepModern extends Component {
  constructor(props, context) {
    super(props);
    const {
      tour
    } = context || {};
    this.state = {};
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.state = {
        currentTour: tour.getTour(),
        currentStep: tour.getStep(),
        readyToStart: false
      };
    }
    this.targetRef = /*#__PURE__*/createRef();
    this.previousIsActive = this.props.open;
    this.onOpenComplete = this.onOpenComplete.bind(this);
    this.onOpenStart = this.onOpenStart.bind(this);
    this.scrollToTarget = this.scrollToTarget.bind(this);
    this.waitForAttachToElement = this.waitForAttachToElement.bind(this);
    this.unsubscribes = [];
  }
  componentDidMount() {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      tour
    } = this.context;
    this._mounted = true;
    if (typeof tour !== 'undefined') {
      const unsubscribe = tour.subscribe((currentStep, currentTour) => {
        if (this._mounted) {
          this.setState({
            currentTour,
            currentStep
          });
        }
      });
      this.unsubscribes = this.unsubscribes.concat(unsubscribe);
      const currentTour = tour.getTour();
      const currentStep = tour.getStep();
      const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);

      // if the tour is active when we mount, we need to kick off the beforeStart.
      // This is because if this is already active on mount, there will be no state updates.
      // That means componentDidUpdate is never run.
      if (isActive) {
        handleCallback(beforeStart || this.waitForAttachToElement, () => {
          this.setState({
            readyToStart: true
          });
          this.childElement = this.getChildElement();
        }, {
          tourKey: currentTour,
          stepKey: currentStep
        });
      }
    }
    const {
      attachTo,
      toggleOnTargetVisibilityChange
    } = this.props;
    if (attachTo && toggleOnTargetVisibilityChange) {
      this.initTargetElementObserver();
    }
  }
  componentDidUpdate(_prevProps, prevState) {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      readyToStart,
      currentStep,
      currentTour
    } = this.state;
    const {
      readyToStart: wasReadyToStart
    } = prevState;
    const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);
    if (isActive && !wasReadyToStart && !readyToStart) {
      handleCallback(beforeStart || this.waitForAttachToElement, () => {
        this.setState({
          readyToStart: true
        });
      }, {
        tourKey: currentTour,
        stepKey: currentStep
      });
    }
    if (!isActive && wasReadyToStart && readyToStart) {
      this.setState({
        readyToStart: false
      });
    }
    if (isActive) {
      this.childElement = this.getChildElement();
    }
    const {
      toggleOnTargetVisibilityChange
    } = this.props;
    if (toggleOnTargetVisibilityChange && readyToStart !== wasReadyToStart) {
      if (readyToStart) {
        this.beginTargetElementObserver();
      } else {
        this.endTargetElementObserver();
      }
    }
  }
  componentWillUnmount() {
    const {
      tour
    } = this.context;
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
    }
    this.endTargetElementObserver();
  }
  getChildElement() {
    const {
      pulser,
      children,
      targetRef
    } = this.props;
    if (pulser && children) {
      // First preference: use explicit targetRef if provided
      if (targetRef !== null && targetRef !== void 0 && targetRef.current) {
        return targetRef.current;
      }
      Raven.captureMessage('UITourStep: missing targetRef for pulser', {
        level: 'warning',
        extra: {
          stepKey: this.props.stepKey,
          tourKey: this.props.tourKey
        }
      });
      console.warn(`UITourStep: missing targetRef for ${this.props.stepKey}`);
    }
    return undefined;
  }

  /**
   * Setup MutationObserver for hide/resume tooltip when target element is removed/re-mounted
   */
  initTargetElementObserver() {
    const {
      attachTo,
      tourKey,
      stepKey
    } = this.props;
    // Store element reference for comparison
    let prevTargetElement = undefined;
    this.targetObserver = new MutationObserver(() => {
      const {
        readyToStart
      } = this.state;
      if (!readyToStart || !this.isActive(tourKey, stepKey)) {
        return;
      }
      const target = getAttachedElement(attachTo);
      const isTargetVisible = isElementVisible(target);
      const isElementRefChanged = target !== prevTargetElement && prevTargetElement !== undefined;
      const isVisibilityChanged = this.wasTargetVisible !== isTargetVisible;

      // If target is removed/resumed or visibility has changed, re-render tour step to hide/resume tour step
      if (isElementRefChanged || isVisibilityChanged) {
        console.warn(`[UITourStep:${stepKey}] Target element changed, updating state`);
        this.wasTargetVisible = isTargetVisible;
        // Store new reference
        prevTargetElement = target;

        // Force re-render to update Popover targeting
        this.setState({});
      }
    });
  }
  beginTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  endTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.disconnect();
    this.wasTargetVisible = undefined;
  }
  isActive(tourKey, stepKey, currentTourKey, currentStepKey) {
    const {
      currentTour,
      currentStep
    } = this.state;
    currentTourKey = currentTourKey || currentTour;
    currentStepKey = currentStepKey || currentStep;
    const isOnTour = tourKey ? tourKey === currentTourKey : !!currentTourKey;
    const isOnStep = stepKey === currentStepKey;
    return isOnTour && isOnStep;
  }
  getStepText({
    steps,
    stepKey
  }) {
    const stepIndex = steps.indexOf(stepKey);
    const totalSteps = steps.length;
    return I18n.text('shepherd-react.stepProgress', {
      currentStep: stepIndex + 1,
      totalSteps
    });
  }
  onOpenComplete() {
    const {
      onOpenComplete
    } = this.props;
    if (onOpenComplete) {
      handleCallback(onOpenComplete, this.scrollToTarget, this.context);
    }
  }
  onOpenStart() {
    const {
      onOpenStart,
      stepKey
    } = this.props;
    if (onOpenStart) {
      onOpenStart();
    }
    const cleanStepKey = hyphenateKey(stepKey);
    focusButtonElement(`${cleanStepKey}-popover`);
  }
  scrollToTarget() {
    const {
      attachTo,
      scrollTo,
      scrollToTarget
    } = this.props;
    if (!scrollTo || this._scrolled) {
      return;
    }
    const target = getAttachedElement(scrollToTarget || attachTo) || this.targetRef.current;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: scrollTo
      });
      this._scrolled = true;
    }
  }
  waitForAttachToElement() {
    const {
      attachTo
    } = this.props;
    if (!attachTo || getAttachedElement(attachTo)) {
      return undefined;
    }
    const isElementGetter = typeof attachTo === 'function';
    const elementGetter = isElementGetter ? attachTo : undefined;
    const elementQuery = !isElementGetter ? attachTo : undefined;
    return waitForElementToAppear({
      elementGetter,
      elementQuery
    });
  }
  render() {
    const _this$props = this.props,
      {
        attachTo,
        children,
        stepKey,
        tourKey,
        content,
        childOnActive,
        onClickCloseButton,
        onCloseStart,
        pulser,
        scrollTo,
        scrollToTarget,
        showCloseButton,
        showSteps,
        stepProgress

        // beforeStart shouldn't be passed to UIPopover
        ,

        width
      } = _this$props,
      extraProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      readyToStart
    } = this.state;
    const {
      tour,
      rootNode
    } = this.context;
    const target = getAttachedElement(attachTo) || null;
    const hasTarget = attachTo ? !!target && isElementVisible(target) : true;
    const isActive = this.isActive(tourKey, stepKey);
    const isActiveAndReadyToStart = isActive && hasTarget && readyToStart;
    if (typeof content === 'undefined' && children) {
      const child = Children.only(children);
      return /*#__PURE__*/cloneElement(child, {
        isStepActive: isActiveAndReadyToStart
      });
    }
    if ( /*#__PURE__*/isValidElement(content) && isActiveAndReadyToStart) {
      return content;
    }
    const config = tour.getConfig();
    const stepText = showSteps && (stepProgress ? I18n.text('shepherd-react.stepProgress', stepProgress) : this.getStepText({
      steps: config.steps,
      stepKey
    }));
    const handleDeactivation = () => {
      if (typeof onClickCloseButton === 'function' && onClickCloseButton(stepKey, tourKey) === false) {
        return null;
      }
      return deactivateTour(tour, tourKey);
    };
    const {
      open = isActiveAndReadyToStart
    } = this.props;
    const cleanStepKey = hyphenateKey(stepKey);
    const inlineSize = width && typeof width === 'number' ? `${width}px` : width;
    const showPulser = readyToStart && pulser;
    const pulserNode = showPulser ? /*#__PURE__*/_jsx(UITourPulserModern, Object.assign({
      target: target || this.childElement
    }, typeof pulser === 'object' && pulser)) : undefined;
    const bodyContent = getContentPart(content, 'body');
    const headerContent = getContentPart(content, 'header');
    const footerContent = getContentPart(content, 'footer');
    const popoverContent = /*#__PURE__*/_jsx(PopoverBody, {
      padding: "0",
      children: /*#__PURE__*/_jsx(ToolBarContext.Provider, {
        value: {
          isFilled: true
        },
        children: /*#__PURE__*/_jsx(OnFillLinkOverride, {
          children: /*#__PURE__*/_jsxs(View, {
            backgroundColor: ONBOARDING_COLOR,
            borderRadius: "400",
            overflow: "hidden",
            paddingInline: "400",
            paddingBlock: "400",
            position: "relative",
            children: [(showCloseButton || headerContent) && /*#__PURE__*/_jsxs(View, {
              as: "header",
              children: [showCloseButton && /*#__PURE__*/_jsx(View, {
                position: "absolute",
                insetBlockStart: "200",
                insetInlineEnd: "200",
                children: /*#__PURE__*/_jsx(OnFillCloseButton, {
                  "aria-label": I18n.text('shepherd-react.close'),
                  onClick: handleDeactivation
                })
              }), headerContent && /*#__PURE__*/_jsx(Text, {
                id: `${cleanStepKey}-popover-title`,
                as: "h4",
                typography: "heading-200",
                color: "text-primary-default",
                children: headerContent
              })]
            }), (bodyContent || pulserNode) && /*#__PURE__*/_jsxs(View, {
              paddingBlockStart: "300",
              children: [bodyContent && /*#__PURE__*/_jsx(Text, {
                as: "p",
                typography: "body-100",
                color: "text-primary-default",
                children: bodyContent
              }), pulserNode]
            }), (footerContent || stepText) && /*#__PURE__*/_jsx(View, {
              as: "footer",
              children: /*#__PURE__*/_jsxs(Flex, {
                paddingBlockStart: "400",
                alignItems: "center",
                children: [stepText && /*#__PURE__*/_jsx(Text, {
                  as: "small",
                  typography: "detail-100",
                  color: "text-primary-subtle",
                  children: stepText
                }), footerContent && /*#__PURE__*/_jsx(View, {
                  marginInlineStart: "auto",
                  children: footerContent
                })]
              })
            })]
          })
        })
      })
    });
    return /*#__PURE__*/_jsx(Popover, Object.assign({
      "data-onboarding-name": "popover",
      _bodyElement: rootNode,
      "aria-labelledby": headerContent ? `${cleanStepKey}-popover-title` : undefined,
      "aria-live": "assertive",
      "data-test-id": `${cleanStepKey}-popover`,
      role: "alertdialog",
      use: "unstyled",
      open: open,
      inlineSize: inlineSize || '350px',
      target: attachTo && hasTarget ? target : undefined,
      arrowColor: ONBOARDING_COLOR,
      borderRadius: "400",
      content: popoverContent,
      onOpenComplete: this.onOpenComplete,
      closeOnOutsideClick: false,
      onOpenChange: showCloseButton ? handleDeactivation : undefined
    }, extraProps, {
      children: /*#__PURE__*/_jsx(TourStepChildren, {
        childOnActive: childOnActive,
        isActive: isActive,
        pulser: pulser,
        readyToStart: readyToStart,
        scrollTo: scrollTo,
        scrollToTarget: scrollToTarget,
        targetRef: this.targetRef,
        target: target,
        open: open,
        onOpenStart: this.onOpenStart,
        onCloseStart: onCloseStart,
        children: children
      }, stepKey)
    }));
  }
}
UITourStepModern.contextType = TourContext;
UITourStepModern.defaultProps = {
  onOpenComplete: emptyFunction
};
const UITourStep = props => {
  if (hasRedesignGate()) {
    return /*#__PURE__*/_jsx(UITourStepModern, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(UITourStepLegacy, Object.assign({}, props));
};
export { UITourStepModern };
export default UITourStep;